import { useCallback, useEffect, useRef } from 'react';
import {
  editLogbookTimeline,
  useLogbookEditedTimeline,
} from '../../../../services/logbook';
import { DriverStatus } from '../../../../types/logbook';

export const TimelineStatusToRowNr: Record<DriverStatus, number> = {
  DRIVING: 2,
  RESTING: 3,
  WORKING: 4,
};

const MOVEMENT_THRESHOLD = 15; // when long press is considered scroll
const LONG_PRESS_DELAY = 400;
const EDGE_THRESHOLD = 40;
const SCROLL_SPEED = 4;

function isTouchEvent(
  e: React.TouchEvent | React.MouseEvent
): e is React.TouchEvent {
  return 'touches' in e.nativeEvent;
}

const getCoords = (
  e: React.TouchEvent<HTMLDivElement> | React.MouseEvent
): { clientX: number; clientY: number } => {
  if (isTouchEvent(e)) {
    const touch = e.nativeEvent.touches[0];
    return {
      clientX: touch.clientX,
      clientY: touch.clientY,
    };
  } else {
    return {
      clientX: e.clientX,
      clientY: e.clientY,
    };
  }
};

export const useMiltiSelectTimeline = () => {
  const timeline = useLogbookEditedTimeline();
  const cachedTimeline = useRef(timeline);

  const scrollRef = useRef<HTMLDivElement>(null);

  const isSelecting = useRef(false);
  const longPressTimeout = useRef<number | null>(null);
  const startTouch = useRef<{ x: number; y: number } | null>(null);
  const hasMovedTooFar = useRef(false);

  const latestTouchX = useRef<number | null>(null);
  const latestTouchY = useRef<number | null>(null);

  const scrollAnimFrame = useRef<number | null>(null);

  const startAutoScroll = () => {
    const step = () => {
      if (
        !isSelecting.current ||
        !latestTouchX.current ||
        !latestTouchY.current ||
        !scrollRef.current
      ) {
        scrollAnimFrame.current = requestAnimationFrame(step);
        return;
      }

      const container = scrollRef.current.parentElement as HTMLDivElement;
      const rect = container.getBoundingClientRect();
      const x = latestTouchX.current;

      if (x - rect.left < EDGE_THRESHOLD) {
        container.scrollLeft -= SCROLL_SPEED;
      } else if (rect.right - x < EDGE_THRESHOLD) {
        container.scrollLeft += SCROLL_SPEED;
      }

      scrollAnimFrame.current = requestAnimationFrame(step);

      selectElement({
        clientX: latestTouchX.current,
        clientY: latestTouchY.current,
      });
    };

    if (scrollAnimFrame.current === null) {
      scrollAnimFrame.current = requestAnimationFrame(step);
    }
  };

  const stopAutoScroll = useCallback(() => {
    if (scrollAnimFrame.current !== null) {
      cancelAnimationFrame(scrollAnimFrame.current);
      scrollAnimFrame.current = null;
    }
  }, []);

  const selectElement = useCallback(
    (touch: Pick<React.Touch, 'clientX' | 'clientY'>) => {
      const element = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      ) as HTMLDivElement | null;

      if (element && element.dataset.editable === 'true') {
        const index = Number(element.dataset.index);

        const newTimeline = [...cachedTimeline.current];
        newTimeline[index] = element.dataset.type as DriverStatus;

        cachedTimeline.current = [...newTimeline];
        editLogbookTimeline(cachedTimeline.current);
      }
    },
    []
  );

  const handleTouchStart = (
    e: React.TouchEvent<HTMLDivElement> | React.MouseEvent
  ) => {
    const { clientX, clientY } = getCoords(e);

    startTouch.current = { x: clientX, y: clientY };
    hasMovedTooFar.current = false;

    longPressTimeout.current = window.setTimeout(() => {
      isSelecting.current = true;
      startAutoScroll();
      selectElement({ clientX, clientY });
    }, LONG_PRESS_DELAY);
  };

  const clearLongPressTimeout = () => {
    if (longPressTimeout.current !== null) {
      clearTimeout(longPressTimeout.current);
      longPressTimeout.current = null;
    }
  };

  const handleTouchEnd = () => {
    // keep existing click functionality
    // so if user wasn't scrolling
    // or wasn't in selection mode, just select current item
    if (
      !isSelecting.current &&
      !hasMovedTooFar.current &&
      latestTouchX.current &&
      latestTouchY.current
    ) {
      selectElement({
        clientX: latestTouchX.current,
        clientY: latestTouchY.current,
      });
    }

    isSelecting.current = false;
    latestTouchX.current = null;
    latestTouchY.current = null;
    stopAutoScroll();
    startTouch.current = null;
    hasMovedTooFar.current = false;
    clearLongPressTimeout();
  };

  const handleTouchMove = (
    e: React.TouchEvent<HTMLDivElement> | React.MouseEvent
  ) => {
    const { clientX, clientY } = getCoords(e);
    latestTouchX.current = clientX;
    latestTouchY.current = clientY;

    // check if user wants to actually scroll and not multi-select
    if (startTouch.current) {
      const dx = clientX - startTouch.current.x;
      const dy = clientY - startTouch.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > MOVEMENT_THRESHOLD && !isSelecting.current) {
        hasMovedTooFar.current = true;
        clearLongPressTimeout();
      }
    }

    if (isSelecting.current) {
      selectElement({ clientX, clientY });
    }
  };

  useEffect(() => {
    const preventDefaultTouchMove = (e: TouchEvent) => {
      if (isSelecting.current) {
        e.preventDefault(); // this stops all scrolling + pull-to-refresh
      }
    };

    document.addEventListener('touchmove', preventDefaultTouchMove, {
      passive: false,
    });

    return () => {
      document.removeEventListener('touchmove', preventDefaultTouchMove);
    };
  }, []);

  useEffect(() => {
    return () => {
      clearLongPressTimeout();
    };
  }, []);

  return {
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    scrollRef,
  };
};
